import { ChangeDetectionStrategy, Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Sort, SortDirection } from '@angular/material/sort';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslationService } from '../../translation.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FwIconModule, FwTooltipModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

export interface AcquisitionSortValue extends Record<string, Sort> {
  acquisition: {
    active: 'label' | 'created' | 'timestamp'
    direction: SortDirection
  }
  file: {
    active: 'description' | 'created' | 'classification' | 'fileType'
    direction: SortDirection
  }
}

@Component({
  selector: 'acquisition-sort',
  styleUrls: ['./acquisition-sort.component.scss'],
  templateUrl: './acquisition-sort.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    useExisting: forwardRef(() => AcquisitionSortComponent),
    multi: true,
  }],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwIconModule,
    FwTooltipModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
  ],
})
export class AcquisitionSortComponent implements ControlValueAccessor {
  readonly disabled$ = new BehaviorSubject(false)
  readonly options = [
    {
      value: 'acquisition',
      options: [
        { value: 'label' },
        { value: 'created' },
        { value: 'timestamp' },
      ],
    }, {
      value: 'file',
      options: [
        { value: 'description' },
        { value: 'created' },
        { value: 'classification' },
        { value: 'fileType' },
      ],
    },
  ];
  readonly tooltip$: Observable<string>
  readonly value$ = new BehaviorSubject<AcquisitionSortValue>({
    acquisition: {
      active: 'created',
      direction: 'asc',
    },
    file: {
      active: 'created',
      direction: 'asc',
    },
  })

  private onChange: (value: AcquisitionSortValue) => void = () => { } // eslint-disable-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => { } // eslint-disable-line @typescript-eslint/no-empty-function

  constructor(
    private translate: TranslationService,
  ) {
    this.tooltip$ = combineLatest([
      this.value$,
      this.translate.getBundle('acquisitionSort'),
    ]).pipe(
      map(([value, messages]) => this.translate.interpolate(
        messages.get('tooltip'),
        messages.get(['acquisition', value.acquisition.active]),
        messages.get(value.acquisition.direction === 'desc' ? 'descending' : 'ascending'),
        messages.get(['file', value.file.active]),
        messages.get(value.file.direction === 'desc' ? 'descending' : 'ascending'),
      )),
    );
  }

  get value(): AcquisitionSortValue {
    return this.value$.value;
  }

  openSortMenu(): void {
    this.onTouched();
  }

  selectSortOption(level: keyof AcquisitionSortValue, active: string): void {
    if (this.disabled$.value) {
      return;
    }
    this.value$.next({
      ...this.value,
      [level]: {
        direction: this.value[level].active === active && this.value[level].direction === 'asc' ? 'desc' : 'asc' as SortDirection,
        active: active,
      },
    });
    this.onChange(this.value);
  }

  writeValue(value: AcquisitionSortValue): void {
    this.value$.next(value);
  }

  registerOnChange(fn: (value: AcquisitionSortValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled$.next(isDisabled);
  }
}
